import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { ProvisioningRequest, ApiError } from '@/types';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { StatusBadge } from '@/components/StatusBadge';
import { formatDate } from '@/lib/utils';
import {
  ArrowLeft,
  Loader2,
  CheckCircle,
  XCircle,
  RefreshCw,
  AlertTriangle,
  Clock,
} from 'lucide-react';

export function AdminRequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [request, setRequest] = useState<ProvisioningRequest | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  // Dialog states
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [sfAccountId, setSfAccountId] = useState('');

  useEffect(() => {
    loadRequest();
  }, [id]);

  const loadRequest = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const data = await api.getAdminRequest(id);
      setRequest(data);
    } catch (error) {
      console.error('Failed to load request:', error);
      navigate('/admin');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!id) return;
    setError('');
    setIsProcessing(true);
    try {
      const result = await api.approveRequest(id);
      if (result.error) {
        setError(result.error);
      }
      await loadRequest();
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.error || 'Failed to approve');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!id) return;
    setError('');
    setIsProcessing(true);
    try {
      await api.rejectRequest(id, rejectReason);
      setShowRejectDialog(false);
      setRejectReason('');
      await loadRequest();
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.error || 'Failed to reject');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRetry = async () => {
    if (!id) return;
    setError('');
    setIsProcessing(true);
    try {
      const result = await api.retryRequest(id);
      if (result.error) {
        setError(result.error);
      }
      await loadRequest();
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.error || 'Failed to retry');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleComplete = async () => {
    if (!id || !sfAccountId.trim()) return;
    setError('');
    setIsProcessing(true);
    try {
      await api.completeRequest(id, sfAccountId.trim());
      setShowCompleteDialog(false);
      setSfAccountId('');
      await loadRequest();
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.error || 'Failed to complete');
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-abby-primary" />
        </div>
      </Layout>
    );
  }

  if (!request) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-gray-600">Request not found</p>
        </div>
      </Layout>
    );
  }

  const canApprove = request.status === 'Submitted';
  const canReject = request.status === 'Submitted';
  const canRetry = request.status === 'Failed';
  const canComplete = request.status === 'Provisioned';

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/admin">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-gray-900">
                {request.payload.Account_Name}
              </h1>
              <StatusBadge status={request.status} />
            </div>
            <p className="text-gray-600 mt-1">
              Created {formatDate(request.created_at)} | Attempts: {request.attempt_count}
            </p>
          </div>
        </div>

        {error && (
          <div className="rounded-lg bg-red-50 p-4 text-sm text-red-600">
            {error}
          </div>
        )}

        {request.last_error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
                <div>
                  <p className="font-medium text-red-800">Provisioning Error</p>
                  <p className="text-sm text-red-600">{request.last_error}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        {(canApprove || canReject || canRetry || canComplete) && (
          <Card className="border-abby-primary/20 bg-abby-primary/5">
            <CardHeader>
              <CardTitle>Actions</CardTitle>
              <CardDescription>Take action on this request</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {canApprove && (
                  <Button onClick={handleApprove} disabled={isProcessing}>
                    {isProcessing ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <CheckCircle className="h-4 w-4 mr-2" />
                    )}
                    Approve & Provision
                  </Button>
                )}
                {canReject && (
                  <Button
                    variant="destructive"
                    onClick={() => setShowRejectDialog(true)}
                    disabled={isProcessing}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                )}
                {canRetry && (
                  <Button onClick={handleRetry} disabled={isProcessing}>
                    {isProcessing ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Retry Provisioning
                  </Button>
                )}
                {canComplete && (
                  <Button onClick={() => setShowCompleteDialog(true)} disabled={isProcessing}>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Mark as Completed
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Request Details */}
        <div className="grid gap-6 md:grid-cols-2">
          {/* Account Information */}
          <Card>
            <CardHeader>
              <CardTitle>Account Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-gray-500">Account Name</Label>
                <p className="text-gray-900 font-medium">{request.payload.Account_Name}</p>
              </div>
              <div>
                <Label className="text-gray-500">Industry</Label>
                <p className="text-gray-900">{request.payload.Industry}</p>
              </div>
              <div>
                <Label className="text-gray-500">Reseller Account ID</Label>
                <p className="text-gray-900 font-mono text-sm">{request.payload.Reseller_Account_Id}</p>
              </div>
              <div>
                <Label className="text-gray-500">Portal UUID</Label>
                <p className="text-gray-900 font-mono text-sm">{request.payload.Reseller_Portal_UUID}</p>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-gray-500">Name</Label>
                <p className="text-gray-900">
                  {request.payload.First_Name} {request.payload.Last_Name}
                </p>
              </div>
              <div>
                <Label className="text-gray-500">Email</Label>
                <p className="text-gray-900">{request.payload.Email}</p>
              </div>
              <div>
                <Label className="text-gray-500">Phone</Label>
                <p className="text-gray-900">{request.payload.Phone || '-'}</p>
              </div>
              <div>
                <Label className="text-gray-500">Address</Label>
                <p className="text-gray-900">
                  {[
                    request.payload.Street,
                    request.payload.City,
                    request.payload.State,
                    request.payload.Postal_Code,
                    request.payload.Country,
                  ]
                    .filter(Boolean)
                    .join(', ') || '-'}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Service Allowance */}
          <Card>
            <CardHeader>
              <CardTitle>Service Allowance</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-abby-primary">
                  {request.payload.AI_Receptionist_Allowance}
                </p>
                <p className="text-sm text-gray-500">AI Receptionist Allowance</p>
              </div>
              <div>
                <Label className="text-gray-500">AI Receptionist Free Trial</Label>
                <p className="text-gray-900">{request.payload.Is_AI_Receptionist_Free_Trial ? 'Yes' : 'No'}</p>
              </div>
            </CardContent>
          </Card>

          {/* Status & Metadata */}
          <Card>
            <CardHeader>
              <CardTitle>Status Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {request.approved_by_email && (
                <div>
                  <Label className="text-gray-500">Approved By</Label>
                  <p className="text-gray-900">{request.approved_by_email}</p>
                  {request.approved_at && (
                    <p className="text-sm text-gray-500">{formatDate(request.approved_at)}</p>
                  )}
                </div>
              )}
              {request.sf_account_id && (
                <div>
                  <Label className="text-gray-500">Salesforce Account ID</Label>
                  <p className="text-gray-900 font-mono">{request.sf_account_id}</p>
                </div>
              )}
              <div>
                <Label className="text-gray-500">Last Updated</Label>
                <p className="text-gray-900">{formatDate(request.updated_at)}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Audit History */}
        {request.audit_history && request.audit_history.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Audit History</CardTitle>
              <CardDescription>Timeline of actions on this request</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {request.audit_history.map((entry) => (
                  <div key={entry.id} className="flex items-start gap-4">
                    <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
                      <Clock className="h-4 w-4 text-gray-500" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">
                        {entry.action.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                      </p>
                      {entry.actor_email && (
                        <p className="text-sm text-gray-500">by {entry.actor_email}</p>
                      )}
                      <p className="text-sm text-gray-400">{formatDate(entry.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reject Dialog */}
        <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject Request</DialogTitle>
              <DialogDescription>
                Provide a reason for rejecting this request. This will be visible to the reseller.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="reason">Reason</Label>
                <Textarea
                  id="reason"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Enter rejection reason..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleReject} disabled={isProcessing}>
                {isProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Reject
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Complete Dialog */}
        <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Complete Request</DialogTitle>
              <DialogDescription>
                Enter the Salesforce Account ID to mark this request as completed.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="sf_account_id">Salesforce Account ID</Label>
                <Input
                  id="sf_account_id"
                  value={sfAccountId}
                  onChange={(e) => setSfAccountId(e.target.value)}
                  placeholder="001XXXXXXXXXXXX"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCompleteDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleComplete} disabled={isProcessing || !sfAccountId.trim()}>
                {isProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Mark as Completed
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
